/**
 * The vehicle watchlist, as a right-side panel over the Vehicle Crossing report.
 *
 * ─── WHY A DRAWER, AND WHY HERE ───
 * It is the same shape as the face report's Registered Faces panel, and for the same reason:
 * both are an operator-maintained registry of identities that changes what an AI event MEANS,
 * both are edited in response to what the report behind them shows, and both are consulted far
 * less often than the table they sit over. Overlaying rather than displacing keeps the
 * crossings — and the filters and scroll position the operator arrived with — untouched
 * underneath. Two registries that behave differently would be two things to learn.
 *
 * `onPlateSeed` closes the loop the other way: the report's plate cells hand a plate straight
 * into this form, which is the same gesture as registering a face from an unknown-face event.
 *
 * ─── WHAT A ROW MEANS ───
 * A listed plate raises an alert on EVERY crossing that reads it, on any camera, whether or not
 * the site has a vehicle alert rule — see the command-center's alertEvaluator. That is a loud
 * thing to switch on by typing in a box, so the panel is explicit about it: severity is shown
 * per entry, an expiry is offered for a temporary ban, and de-listing is a toggle that keeps
 * the record rather than a delete that loses who listed it and why.
 *
 * ─── PLATES ───
 * The node normalises what it is given (uppercase, separators stripped) and matches on that, so
 * "mh12 ab 1234" and "MH12AB1234" are one entry. What the operator typed is kept for display,
 * because that is the form they will scan the list for.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlusIcon, MagnifyingGlassIcon, TrashIcon, PencilSquareIcon,
  XMarkIcon, NoSymbolIcon, TruckIcon,
} from '@heroicons/react/24/outline';
import { useApi } from '../../api/ApiProvider';
import type { VehicleBlacklistEntry, VehicleBlacklistInput } from '../../api/contracts';
import ConfirmDialog from '../common/ConfirmDialog';

/** The same normalisation the node applies, so the form can preview what will be matched. */
export function canonicalPlate(raw: string): string {
  return String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Severity of the alert a sighting raises.
 *
 * Tinted with the numeric Tailwind palette rather than the theme's status tokens: those are
 * bare `var()` colours, and Tailwind v3 emits NO rule at all for an opacity modifier on one of
 * those — `bg-warning/15` compiles to nothing. The report's own badges next door use these same
 * numeric classes for the same reason.
 */
const SEVERITIES: Array<{ value: string; label: string; chip: string; hint: string }> = [
  { value: 'critical', label: 'Critical', chip: 'bg-red-500/15 text-red-500', hint: 'Interrupt the operator. The default, and what a watchlist is for.' },
  { value: 'warning', label: 'Warning', chip: 'bg-amber-500/15 text-amber-500', hint: 'Raise it, but below the life-safety detections.' },
  { value: 'normal', label: 'Log only', chip: 'bg-blue-500/15 text-blue-500', hint: 'Recorded and visible, but it will not compete for attention.' },
];

const severityOf = (value?: string) => SEVERITIES.find((s) => s.value === value);

const EMPTY_FORM: VehicleBlacklistInput = {
  plate_number: '', reason: '', category: '', notes: '', severity: 'critical', expires_at: '',
};

/** An entry whose expiry has passed is still a row, but it no longer matches anything. */
function isExpired(entry: VehicleBlacklistEntry): boolean {
  return !!entry.expires_at && new Date(entry.expires_at).getTime() <= Date.now();
}

function formatDay(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` in LOCAL time, not an ISO string. */
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** One labelled control. The drawer is 28rem, so every field is full width and stacked. */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

const INPUT = 'w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] '
  + 'text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none';

export interface VehicleBlacklistPanelProps {
  /**
   * A plate to pre-fill the register form with, handed over from a crossing the operator is
   * looking at. Changing it opens the form — that IS the gesture ("blacklist this vehicle"),
   * so requiring a second click to open the editor would be a step for nothing.
   */
  seedPlate?: string | null;
  /** Cleared once consumed, so re-seeding the same plate re-opens the form. */
  onSeedConsumed?: () => void;
  /** Told after any change, so the report can refetch rows whose verdict may now differ. */
  onChanged?: () => void;
  /** False hides every mutating control — the panel becomes a read-only list. */
  canEdit?: boolean;
  /** Dismiss the panel. */
  onClose?: () => void;
}

export default function VehicleBlacklistPanel({
  seedPlate = null,
  onSeedConsumed,
  onChanged,
  canEdit = true,
  onClose,
}: VehicleBlacklistPanelProps) {
  const { vehicleBlacklist } = useApi();

  const [entries, setEntries] = useState<VehicleBlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VehicleBlacklistInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<VehicleBlacklistEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The whole list in one read. A watchlist is a few hundred plates at the very most —
      // the server paginates, but paging a list this size would cost the operator a
      // client-side search that works across all of it.
      const res = await vehicleBlacklist.list({ limit: 200 });
      setEntries(Array.isArray(res?.data) ? res.data : []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Could not load the vehicle watchlist');
    } finally {
      setLoading(false);
    }
  }, [vehicleBlacklist]);

  useEffect(() => { void load(); }, [load]);

  // A plate handed over from a crossing opens the form with it filled in.
  useEffect(() => {
    if (!seedPlate || !canEdit) return;
    setEditingId(null);
    setForm({ ...EMPTY_FORM, plate_number: seedPlate });
    setFormError(null);
    setFormOpen(true);
    onSeedConsumed?.();
  }, [seedPlate, canEdit, onSeedConsumed]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return entries;
    const canonical = canonicalPlate(term);
    return entries.filter((e) =>
      (canonical && e.plate_number.includes(canonical))
      || (e.plate_display || '').toLowerCase().includes(term)
      || (e.reason || '').toLowerCase().includes(term)
      || (e.category || '').toLowerCase().includes(term));
  }, [entries, search]);

  const activeCount = useMemo(
    () => entries.filter((e) => e.is_active && !isExpired(e)).length,
    [entries],
  );

  function openAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(entry: VehicleBlacklistEntry) {
    setEditingId(entry.id);
    setForm({
      plate_number: entry.plate_display || entry.plate_number,
      reason: entry.reason || '',
      category: entry.category || '',
      notes: entry.notes || '',
      severity: entry.severity || 'critical',
      expires_at: toLocalInput(entry.expires_at),
    });
    setFormError(null);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  }

  async function save() {
    const typed = String(form.plate_number || '').trim();
    const canonical = canonicalPlate(typed);
    // Checked here as well as on the node: a plate too short to identify a vehicle would be
    // matched by nothing, and the operator would have no way to tell that from a working entry.
    if (canonical.length < 3) {
      setFormError('A plate needs at least three letters or digits.');
      return;
    }

    // Registering a plate that is already listed is an UPSERT on the node, so it would silently
    // overwrite the existing entry's reason and severity. Say so first — the operator almost
    // always meant to edit the row they could not find.
    if (!editingId) {
      const clash = entries.find((e) => e.plate_number === canonical);
      if (clash) {
        setFormError(
          `${clash.plate_display || clash.plate_number} is already listed`
          + `${clash.reason ? ` (${clash.reason})` : ''}. Saving will replace that entry.`);
        // Not a hard stop: replacing an entry is a perfectly reasonable thing to want.
        // Pointing `editingId` at the existing row turns the second press into an edit of it
        // rather than a blind overwrite.
        setEditingId(clash.id);
        return;
      }
    }

    setSaving(true);
    try {
      const payload: VehicleBlacklistInput = {
        plate_number: canonical,
        plate_display: typed,
        reason: form.reason || null,
        category: form.category || null,
        notes: form.notes || null,
        severity: form.severity || 'critical',
        // datetime-local gives local wall-clock with no zone; Date resolves it in the
        // browser's zone, which is the one the operator typed it in.
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };

      if (editingId) await vehicleBlacklist.update(editingId, payload);
      else await vehicleBlacklist.add(payload);

      closeForm();
      await load();
      onChanged?.();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Could not save this entry');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(entry: VehicleBlacklistEntry) {
    try {
      await vehicleBlacklist.update(entry.id, { is_active: !entry.is_active });
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || 'Could not update this entry');
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      await vehicleBlacklist.remove(confirmDelete.id);
      setConfirmDelete(null);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || 'Could not remove this entry');
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md border-l border-border bg-card shadow-2xl overflow-y-auto">
      {/* ── Head ── */}
      <div className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur px-5 py-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            Blacklisted Vehicles
            {activeCount > 0 && (
              <span className="rounded-md bg-red-500/15 px-2 py-0.5 text-[11px] font-bold text-red-500">
                {activeCount} active
              </span>
            )}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Said once, at the top, because it is the consequence of everything below it. */}
        <p className="mt-1 text-[11px] text-muted-foreground">
          A listed plate alerts on every crossing that reads it, on any camera — with or without
          a vehicle alert rule.
        </p>

        {canEdit && !formOpen && (
          <button
            onClick={openAdd}
            className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-brand text-[13px] font-medium text-white hover:bg-brand-dark transition-colors"
          >
            <PlusIcon className="h-4 w-4" /> Register Blacklist Vehicle
          </button>
        )}

        <div className="relative mt-3">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plate, reason or category…"
            className={`${INPUT} pl-8`}
          />
        </div>
      </div>

      <div className="p-5">
        {/* ── Register / edit ── */}
        {formOpen && canEdit && (
          <div className="mb-4 rounded-xl border border-border bg-background p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-[13px] font-semibold text-foreground">
                {editingId ? 'Edit entry' : 'Register a blacklisted vehicle'}
              </h3>
              <button onClick={closeForm} aria-label="Cancel" className="rounded p-1 hover:bg-muted">
                <XMarkIcon className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            <div className="mt-3 space-y-3">
              <Field
                label="Number plate"
                // Shown because the stored key is not what was typed, and an operator who
                // cannot see that will not understand why "MH-12" and "MH 12" are one entry.
                hint={canonicalPlate(form.plate_number)
                  ? `Matched as ${canonicalPlate(form.plate_number)}`
                  : 'Spacing and dashes are ignored when matching.'}
              >
                <input
                  value={form.plate_number}
                  onChange={(e) => setForm({ ...form, plate_number: e.target.value })}
                  placeholder="MH 12 AB 1234"
                  autoFocus
                  className={`${INPUT} font-mono uppercase`}
                />
              </Field>

              <Field label="Reason">
                <input
                  value={form.reason || ''}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Why this vehicle is listed"
                  className={INPUT}
                />
              </Field>

              <Field label="Category">
                <input
                  value={form.category || ''}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g. ex-contractor"
                  list="vehicle-blacklist-categories"
                  className={INPUT}
                />
                <datalist id="vehicle-blacklist-categories">
                  {[...new Set(entries.map((e) => e.category).filter(Boolean))].map((c) => (
                    <option key={c as string} value={c as string} />
                  ))}
                </datalist>
              </Field>

              <Field label="Alert severity" hint={severityOf(form.severity)?.hint}>
                <select
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value })}
                  className={INPUT}
                >
                  {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>

              <Field label="Expires (optional)" hint="Leave empty to list the vehicle indefinitely.">
                <input
                  type="datetime-local"
                  value={form.expires_at || ''}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className={INPUT}
                />
              </Field>

              <Field label="Notes">
                <input
                  value={form.notes || ''}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything the next operator needs to know"
                  className={INPUT}
                />
              </Field>
            </div>

            {formError && (
              <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-[12px] text-amber-500">
                {formError}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeForm}
                className="h-9 rounded-lg border border-border px-4 text-[13px] text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="h-9 rounded-lg bg-brand px-4 text-[13px] font-medium text-white hover:bg-brand-dark disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Register'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <p className="mb-3 rounded-lg bg-red-500/10 px-3 py-2 text-[12px] text-red-500">{error}</p>
        )}

        {/* ── List ── */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
          </div>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {entries.length === 0
              ? 'No vehicles are blacklisted yet. Register one above, or from a crossing in the report.'
              : 'No entry matches that search.'}
          </p>
        ) : (
          <div className="space-y-3">
            {visible.map((entry) => {
              const expired = isExpired(entry);
              const live = entry.is_active && !expired;
              const sev = severityOf(entry.severity);
              return (
                <div key={entry.id} className="rounded-xl border border-border bg-background hover:bg-muted/50 transition-colors">
                  <div className="flex items-start gap-3 p-3">
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${
                      live ? 'bg-red-500/15' : 'bg-muted'
                    }`}>
                      <TruckIcon className={`h-6 w-6 ${live ? 'text-red-500' : 'text-muted-foreground'}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className={`truncate font-mono text-[13px] font-semibold ${
                        live ? 'text-foreground' : 'text-muted-foreground line-through'
                      }`}>
                        {entry.plate_display || entry.plate_number}
                      </p>

                      <div className="mt-0.5 flex flex-wrap items-center gap-2">
                        {/* Three states, not two. "Expired" is an entry that looks listed and
                            matches nothing — the failure an operator must be able to see. */}
                        <span className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-bold uppercase ${
                          live ? 'bg-emerald-500/15 text-emerald-500'
                            : expired ? 'bg-amber-500/15 text-amber-500'
                              : 'bg-gray-500/15 text-gray-500'
                        }`}
                          title={expired ? `Expired ${formatDay(entry.expires_at)}` : undefined}
                        >
                          {live ? 'Watching' : expired ? 'Expired' : 'Off'}
                        </span>
                        <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${sev?.chip || 'bg-gray-500/15 text-gray-500'}`}>
                          {sev?.label || entry.severity}
                        </span>
                        {entry.category && (
                          <span className="whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-500/15 text-muted-foreground">
                            {entry.category}
                          </span>
                        )}
                      </div>

                      {entry.reason && (
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{entry.reason}</p>
                      )}
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        Added {formatDay(entry.created_at)}{entry.added_by ? ` by ${entry.added_by}` : ''}
                      </p>
                    </div>

                    {canEdit && (
                      <div className="flex flex-shrink-0 items-center">
                        <button
                          onClick={() => openEdit(entry)}
                          title="Edit"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleActive(entry)}
                          title={entry.is_active ? 'Stop watching (keeps the record)' : 'Resume watching'}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-muted ${
                            entry.is_active ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <NoSymbolIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(entry)}
                          title="Remove from the list"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove this plate from the watchlist?"
        message={
          <>
            <span className="font-mono font-semibold text-foreground">
              {confirmDelete?.plate_display || confirmDelete?.plate_number}
            </span>{' '}
            will stop raising alerts. Crossings it already flagged keep their record — only the
            listing goes. To pause it instead and keep the reason, use the block toggle.
          </>
        }
        confirmLabel="Remove"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
